import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Mail, Phone, MessageSquare, ArrowRight, Loader2 } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    notes: ''
  });

  // Redirect if cart is empty
  if (cart.length === 0) {
    navigate('/produits');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customerName.trim()) {
      toast.error('Veuillez entrer votre nom');
      return;
    }
    if (!formData.customerEmail.trim()) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    if (!formData.customerPhone.trim()) {
      toast.error('Veuillez entrer votre téléphone');
      return;
    }

    setLoading(true);

    try {
      // Prepare order data
      const orderData = {
        customerName: formData.customerName.trim(),
        customerEmail: formData.customerEmail.trim(),
        customerPhone: formData.customerPhone.trim(),
        items: cart.map(item => ({
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        notes: formData.notes.trim() || undefined
      };

      // Create order
      const response = await axios.post(`${API}/orders`, orderData);

      if (response.data.success) {
        // Show success message
        toast.success('✅ Commande validée avec succès !', {
          description: 'Vous allez être redirigé vers la page de confirmation...'
        });

        // Clear cart
        clearCart();

        // Redirect to confirmation page after a short delay
        setTimeout(() => {
          navigate(`/commande/confirmation/${response.data.order.id}`, {
            state: { order: response.data.order }
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de la création de la commande');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#2C1810] mb-2">
            Finaliser la commande
          </h1>
          <p className="text-[#5D4037]">
            Remplissez vos informations pour récupérer votre commande en magasin
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8">
              <h2 className="text-2xl font-serif font-bold text-[#2C1810] mb-6">
                Vos informations
              </h2>

              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="customerName" className="block text-sm font-medium text-[#2C1810] mb-2">
                    Nom complet *
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
                    <input
                      type="text"
                      id="customerName"
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      placeholder="Votre nom et prénom"
                      className="w-full pl-12 pr-4 py-3 border-2 border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="customerEmail" className="block text-sm font-medium text-[#2C1810] mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
                    <input
                      type="email"
                      id="customerEmail"
                      value={formData.customerEmail}
                      onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                      placeholder="votre.email@exemple.com"
                      className="w-full pl-12 pr-4 py-3 border-2 border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="customerPhone" className="block text-sm font-medium text-[#2C1810] mb-2">
                    Téléphone *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
                    <input
                      type="tel"
                      id="customerPhone"
                      value={formData.customerPhone}
                      onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                      placeholder="06 12 34 56 78"
                      className="w-full pl-12 pr-4 py-3 border-2 border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent"
                      required
                    />
                  </div>
                  <p className="text-sm text-[#5D4037] mt-1">
                    Nous vous appellerons quand votre commande sera prête
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-[#2C1810] mb-2">
                    Notes (optionnel)
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 text-[#A67B5B]" size={20} />
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Des instructions spéciales ? Dites-nous !"
                      rows={4}
                      className="w-full pl-12 pr-4 py-3 border-2 border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E] focus:border-transparent resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-8 flex items-center justify-center gap-2 px-6 py-4 bg-[#C9A96E] text-white rounded-full hover:bg-[#A67B5B] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Traitement en cours...
                  </>
                ) : (
                  <>
                    Confirmer la commande
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <h2 className="text-2xl font-serif font-bold text-[#2C1810] mb-6">
                Votre commande
              </h2>

              {/* Items */}
              <div className="space-y-4 mb-6 max-h-80 overflow-y-auto">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-[#2C1810] text-sm">{item.name}</p>
                      <p className="text-xs text-[#A67B5B]">{item.brand}</p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-sm text-[#5D4037]">Qté: {item.quantity}</span>
                        <span className="font-semibold text-[#C9A96E]">
                          {(item.price * item.quantity).toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t border-[#E8DDD4] pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-semibold text-[#2C1810]">Total</span>
                  <span className="text-3xl font-bold text-[#C9A96E]">
                    {getCartTotal().toFixed(2)}€
                  </span>
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-[#F5F0EB] rounded-lg space-y-3">
                <div className="flex items-start gap-2">
                  <ShoppingBag className="text-[#C9A96E] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-[#2C1810] text-sm">Retrait en magasin</p>
                    <p className="text-xs text-[#5D4037] mt-1">
                      1 rue Albert de Mun<br />
                      92600 Asnières-sur-Seine
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="text-[#C9A96E] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-semibold text-[#2C1810] text-sm">Nous vous appellerons</p>
                    <p className="text-xs text-[#5D4037] mt-1">
                      Quand votre commande sera prête pour le retrait
                    </p>
                  </div>
                </div>
                <div className="border-t border-[#D4C4B5] pt-3 mt-3">
                  <p className="text-sm text-[#5D4037] text-center">
                    💳 <strong>Paiement en magasin</strong> lors du retrait
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
